/**
 * Centralized storage utility for managing authentication and user data
 * All localStorage operations should go through these functions
 */

import { deleteAuthTokenCookie, deleteRefreshTokenCookie, deleteSessionKeyCookie } from "./clientCookie";

// Check if running in browser
const isBrowser = typeof window !== "undefined";
let TAB_ID: string | null = null;

if (isBrowser) {
  TAB_ID = sessionStorage.getItem("tabId");
  if (!TAB_ID) {
    TAB_ID = Date.now().toString();
    sessionStorage.setItem("tabId", TAB_ID);
  }
}
/**
 * Authentication Token Management
 */
export const storage = {
  // AUTH TOKEN
  setAuthToken: (token: string): void => {
    if (isBrowser) {
      localStorage.setItem("authToken", token);

      localStorage.setItem("auth-event", JSON.stringify({
        type: "LOGIN",
        time: Date.now(),
        tabId: TAB_ID
      }));
    }
  },

  getAuthToken: (): string | null => {
    if (!isBrowser) return null;
    return localStorage.getItem("authToken");
  },

  clearAuthToken: (): void => {
    if (isBrowser) {
      localStorage.removeItem("authToken");
    }
  },

  // REFRESH TOKEN
  setRefreshToken: (token: string): void => {
    if (isBrowser) {
      localStorage.setItem("refreshToken", token);
    }
  },

  getRefreshToken: (): string | null => {
    if (!isBrowser) return null;
    return localStorage.getItem("refreshToken");
  },

  clearRefreshToken: (): void => {
    if (isBrowser) {
      localStorage.removeItem("refreshToken");
    }
  },

  // USER ID
  setUserId: (userId: string | number): void => {
    if (isBrowser) {
      localStorage.setItem("userId", String(userId));
    }
  },

  getUserId: (): string | null => {
    if (!isBrowser) return null;
    return localStorage.getItem("userId");
  },

  clearUserId: (): void => {
    if (isBrowser) {
      localStorage.removeItem("userId");
    }
  },

  // USER DATA
  setUserData: (userData: any): void => {
    if (isBrowser) {
      localStorage.setItem("userData", JSON.stringify(userData));
    }
  },

  getUserData: (): any => {
    if (!isBrowser) return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  },

  clearUserData: (): void => {
    if (isBrowser) {
      localStorage.removeItem("userData");
    }
  },

  // USER (legacy, keeping for compatibility)
  setUser: (user: any): void => {
    if (isBrowser) {
      localStorage.setItem("user", JSON.stringify(user));
    }
  },

  getUser: (): any => {
    if (!isBrowser) return null;
    try {
      const data = localStorage.getItem("user");
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error parsing user:", error);
      return null;
    }
  },

  clearUser: (): void => {
    if (isBrowser) {
      localStorage.removeItem("user");
    }
  },

  // PERMISSIONS
  setPermissions: (data: Record<string, any>): void => {
    if (isBrowser) localStorage.setItem("permissions", JSON.stringify(data));
  },
  getPermissions: (): Record<string, any> | null => {
    if (!isBrowser) return null;
    try {
      const raw = localStorage.getItem("permissions");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  },
  clearPermissions: (): void => {
    if (isBrowser) localStorage.removeItem("permissions");
  },

  // LOGOUT - Clear all authentication data
  logout: (): void => {
    if (isBrowser) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("userData");
      localStorage.removeItem("user");
      localStorage.removeItem("permissions");

      deleteAuthTokenCookie();
      deleteRefreshTokenCookie();
      deleteSessionKeyCookie();

      localStorage.setItem("auth-event", JSON.stringify({
        type: "LOGOUT",
        time: Date.now(),
        tabId: TAB_ID
      }));
    }
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    if (!isBrowser) return false;
    return !!localStorage.getItem("authToken");
  },
};

export default storage;
